import React, { useEffect } from 'react';

import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import {
  Modal,
  Form,
  Input,
  Select,
  Space,
  Button,
  Divider,
  Typography,
  Tag,
} from 'antd';

import { GeneratedAd, SemanticUnit } from '../types';

const { TextArea } = Input;
const { Text } = Typography;

interface AdEditModalProps {
  visible: boolean;
  ad: GeneratedAd | null;
  semanticUnits: SemanticUnit[];
  onCancel: () => void;
  onSubmit: (ad: GeneratedAd) => void;
}

interface FormValues {
  semanticUnitId: string;
  platform: 'yandex_direct' | 'vk';
  headline: string;
  subheadline: string;
  text: string;
  imageUrl: string;
  imageText: string;
  quickLinks: { title: string; url: string }[];
  clarifications: string[];
}

const AdEditModal: React.FC<AdEditModalProps> = ({
  visible,
  ad,
  semanticUnits,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (visible && ad) {
      form.setFieldsValue({
        semanticUnitId: ad.semanticUnitId,
        platform: ad.platform,
        headline: ad.headline,
        subheadline: ad.subheadline,
        text: ad.text,
        imageUrl: ad.imageUrl,
        imageText: ad.imageText,
        quickLinks: ad.quickLinks.length > 0 ? ad.quickLinks : [{ title: '', url: '' }],
        clarifications: ad.clarifications,
      });
    }
  }, [visible, ad, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (!ad) return;

      const updatedAd: GeneratedAd = {
        ...ad,
        semanticUnitId: values.semanticUnitId,
        platform: values.platform,
        headline: values.headline,
        subheadline: values.subheadline,
        text: values.text,
        imageUrl: values.imageUrl,
        imageText: values.imageText,
        quickLinks: values.quickLinks?.filter(l => l.title && l.url) || [],
        clarifications: values.clarifications?.filter(Boolean) || [],
      };

      onSubmit(updatedAd);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const getStatusColor = (status: GeneratedAd['status']) => {
    const colors = {
      draft: 'default',
      pending: 'processing',
      sent: 'blue',
      active: 'green',
      rejected: 'red',
      error: 'error',
    };
    return colors[status];
  };

  const getStatusText = (status: GeneratedAd['status']) => {
    const texts = {
      draft: 'Черновик',
      pending: 'Ожидает',
      sent: 'Отправлено',
      active: 'Активно',
      rejected: 'Отклонено',
      error: 'Ошибка',
    };
    return texts[status];
  };

  return (
    <Modal
      title={
        <Space>
          <span>Редактирование объявления</span>
          {ad && (
            <>
              <Tag color={ad.platform === 'yandex_direct' ? 'orange' : 'blue'}>
                {ad.platform === 'yandex_direct' ? 'Яндекс Директ' : 'ВКонтакте'}
              </Tag>
              <Tag color={getStatusColor(ad.status)}>
                {getStatusText(ad.status)}
              </Tag>
            </>
          )}
        </Space>
      }
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      width={700}
      okText="Сохранить"
      cancelText="Отмена"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="semanticUnitId"
          label="Смысловая единица"
          rules={[{ required: true, message: 'Выберите смысловую единицу' }]}
        >
          <Select
            placeholder="Выберите смысловую единицу"
            options={semanticUnits.map(su => ({
              value: su.id,
              label: su.title,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="platform"
          label="Платформа"
          rules={[{ required: true, message: 'Выберите платформу' }]}
        >
          <Select
            options={[
              { value: 'yandex_direct', label: 'Яндекс Директ' },
              { value: 'vk', label: 'ВКонтакте' },
            ]}
          />
        </Form.Item>

        <Divider orientation="left">Тексты объявления</Divider>

        <Form.Item
          name="headline"
          label="Заголовок"
          rules={[{ required: true, message: 'Введите заголовок' }]}
        >
          <Input placeholder="Основной заголовок объявления" maxLength={56} showCount />
        </Form.Item>

        <Form.Item
          name="subheadline"
          label="Подзаголовок"
        >
          <Input placeholder="Дополнительный заголовок" maxLength={30} showCount />
        </Form.Item>

        <Form.Item
          name="text"
          label="Текст объявления"
          rules={[{ required: true, message: 'Введите текст' }]}
        >
          <TextArea
            rows={4}
            placeholder="Основной текст объявления"
            maxLength={81}
            showCount
          />
        </Form.Item>

        <Divider orientation="left">Изображение</Divider>

        <Form.Item
          name="imageUrl"
          label="URL изображения"
        >
          <Input placeholder="https://example.com/image.jpg" />
        </Form.Item>

        <Form.Item
          name="imageText"
          label="Текст на изображении"
        >
          <Input placeholder="Текст, который будет на картинке" maxLength={20} showCount />
        </Form.Item>

        <Divider orientation="left">Быстрые ссылки</Divider>

        <Form.List name="quickLinks">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={[name, 'title']}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="Название" style={{ width: 200 }} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'url']}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="URL" style={{ width: 300 }} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Добавить быструю ссылку
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Divider orientation="left">Уточнения</Divider>

        <Form.List name="clarifications">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={name}
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input placeholder="Уточнение (например: Гарантия результата)" style={{ width: 400 }} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add('')} block icon={<PlusOutlined />}>
                  Добавить уточнение
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        {ad?.errorMessage && (
          <div style={{ marginTop: 16 }}>
            <Text type="danger">Ошибка: {ad.errorMessage}</Text>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default AdEditModal;
